import React, { useEffect, useMemo, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { StatCardCaption, StatCardCaptionProps } from './stat-card-caption';
import { Typography } from '@packages/ui/shared';
import { useTranslation } from 'react-i18next';
import colors from '@packages/core/styles/colors';
import { SkeletonLoader } from '@web/components/skeleton-loader';

const useStyles = createUseStyles({
    card: {
        border: `1px solid ${colors.grayThree}`,
        borderRadius: 4,
        padding: '16px 24px',
        minHeight: 176,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        backgroundColor: '#FFFFFF',
        transition: 'transform 0.2s ease',
        '@media (max-width: 1200px)': {
            padding: '16px 20px',
            minHeight: 164,
        },
        '@media (max-width: 992px)': {
            padding: '16px 18px',
            minHeight: 148,
        },
        '@media (max-width: 640px)': {
            padding: '12px 16px',
            minHeight: 132,
        },
    },
    content: {
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        height: '100%',
        '@media (max-width: 640px)': {
            gap: 12,
        },
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        minHeight: 24,
    },
    valueWrapper: {
        minHeight: 48,
        '@media (max-width: 992px)': {
            minHeight: 40,
        },
        '@media (max-width: 640px)': {
            minHeight: 32,
        },
    },
    valueSkeleton: {
        height: 36,
        marginTop: 8,
        '@media (max-width: 1200px)': {
            height: 32,
        },
        '@media (max-width: 992px)': {
            height: 28,
        },
        '@media (max-width: 640px)': {
            height: 24,
        },
    },
    unitWrapper: {
        marginLeft: 8,
        '@media (max-width: 640px)': {
            marginLeft: 4,
        },
    },
    captionsWrapper: {
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        marginTop: 'auto',
        '@media (max-width: 640px)': {
            gap: 8,
        },
    },
    captionSkeletonRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        width: '100%',
    },
    captionSkeletonIcon: {
        flexShrink: 0,
    },
    captionSkeletonText: {
        flexGrow: 1,
        marginTop: 6,
    },
    captionSkeletonValue: {
        flexShrink: 0,
        marginTop: 6,
    },
});

type StatCardProps = {
    captions?: StatCardCaptionProps | StatCardCaptionProps[];
    className?: string;
    captionsMarginTop?: number;
    isLoading?: boolean;
    title: string;
    unitOfMeasure?: string;
    value?: string;
};

export function StatCard({
    captions,
    className,
    captionsMarginTop,
    isLoading = false,
    title,
    unitOfMeasure,
    value,
}: StatCardProps) {
    const { t } = useTranslation();
    const classes = useStyles();
    const shouldRenderValueSection = isLoading || Boolean(value);
    const [viewportWidth, setViewportWidth] = useState(() =>
        typeof window !== 'undefined' ? window.innerWidth : 1440
    );

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }

        const handleResize = () => {
            setViewportWidth(window.innerWidth);
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    const valueVariant = useMemo(() => {
        if (viewportWidth <= 640) {
            return 'h4';
        }

        if (viewportWidth <= 992) {
            return 'h3';
        }

        if (viewportWidth <= 1280) {
            return 'h2';
        }

        return 'h1';
    }, [viewportWidth]);

    const titleVariant = useMemo(() => {
        if (viewportWidth <= 640) {
            return 'caption';
        }

        if (viewportWidth <= 992) {
            return 'labelRegular';
        }

        return 'label';
    }, [viewportWidth]);

    const unitVariant = useMemo(() => {
        switch (valueVariant) {
            case 'h1':
                return 'h5';
            case 'h2':
                return 'h6';
            case 'h3':
                return 'caption3';
            default:
                return 'caption';
        }
    }, [valueVariant]);

    const captionItems = useMemo(() => {
        if (!captions) {
            return [] as StatCardCaptionProps[];
        }

        return Array.isArray(captions) ? captions : [captions];
    }, [captions]);

    const cardClassName = [classes.card, className].filter(Boolean).join(' ');

    return (
        <div className={cardClassName}>
            <div className={classes.content}>
                <div className={classes.header}>
                    <Typography color="blueOne" variant={titleVariant}>
                        {t(title)}
                    </Typography>
                </div>
                {shouldRenderValueSection && (
                    <div className={classes.valueWrapper}>
                        {isLoading ? (
                            <SkeletonLoader type="text" className={classes.valueSkeleton} width="65%" />
                        ) : (
                            value && (
                                <Typography variant={valueVariant} color="blueOne">
                                    {t(value)}
                                    {unitOfMeasure && (
                                        <span className={classes.unitWrapper}>
                                            <Typography variant={unitVariant} color="blueOne">
                                                {unitOfMeasure}
                                            </Typography>
                                        </span>
                                    )}
                                </Typography>
                            )
                        )}
                    </div>
                )}
                {captionItems.length > 0 && (
                    <div
                        className={classes.captionsWrapper}
                        style={
                            captionsMarginTop !== undefined
                                ? { marginTop: captionsMarginTop }
                                : shouldRenderValueSection
                                  ? undefined
                                  : { marginTop: 0 }
                        }
                    >
                        {captionItems.map((captionProps, index) => {
                            if (isLoading) {
                                return (
                                    <div className={classes.captionSkeletonRow} key={`stat-card-skeleton-${index}`}>
                                        {captionProps.showIcon && (
                                            <SkeletonLoader type="icon" className={classes.captionSkeletonIcon} />
                                        )}
                                        <SkeletonLoader
                                            type="text"
                                            className={classes.captionSkeletonText}
                                            width="100%"
                                        />
                                        {captionProps.value && (
                                            <SkeletonLoader
                                                type="text"
                                                width={64}
                                                height={12}
                                                className={classes.captionSkeletonValue}
                                            />
                                        )}
                                    </div>
                                );
                            }

                            return <StatCardCaption key={index + captionProps.text} {...captionProps} />;
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}
